import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Timer, Plus, Pencil, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function SlotManagementPage() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slot_name: '', start_time: '09:00', end_time: '12:00', max_orders: 50, is_active: true, days: [...DAYS] });

  useEffect(() => { fetchSlots(); }, []);

  const fetchSlots = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/slots`, { withCredentials: true });
      setSlots(data.slots || []);
    } catch { toast.error('Failed to load slots'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.slot_name) { toast.error('Slot name is required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/api/seller/slots/${editing.slot_id}`, form, { withCredentials: true });
        toast.success('Slot updated');
      } else {
        await axios.post(`${API}/api/seller/slots`, form, { withCredentials: true });
        toast.success('Slot created');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchSlots();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (sid) => {
    if (!window.confirm('Delete this slot?')) return;
    try {
      await axios.delete(`${API}/api/seller/slots/${sid}`, { withCredentials: true });
      toast.success('Slot deleted');
      fetchSlots();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleDay = (day) => setForm(f => ({ ...f, days: f.days.includes(day) ? f.days.filter(d => d !== day) : [...f.days, day] }));

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="slot-management-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Delivery Slot Management</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Configure delivery time slots and capacity</p>
          </div>
          <Button data-testid="add-slot-btn" onClick={() => { setEditing(null); setForm({ slot_name: '', start_time: '09:00', end_time: '12:00', max_orders: 50, is_active: true, days: [...DAYS] }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Slot
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : slots.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <Timer size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No delivery slots configured yet.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {slots.map(s => (
              <div key={s.slot_id} className={`bg-white rounded-xl border p-5 ${s.is_active ? 'border-[#e5e5ea]' : 'border-red-200 opacity-60'}`} data-testid={`slot-${s.slot_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">{s.slot_name}</h3>
                    <p className="text-lg font-mono text-[#7d64ff]">{s.start_time} — {s.end_time}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setForm({ slot_name: s.slot_name, start_time: s.start_time, end_time: s.end_time, max_orders: s.max_orders, is_active: s.is_active, days: s.days || [...DAYS] }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(s.slot_id)} className="text-red-500"><Trash size={14} /></Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-[#6b6b8a]">Max: {s.max_orders} orders</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="flex gap-1">
                  {DAYS.map(d => <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded ${(s.days || []).includes(d) ? 'bg-[#7d64ff]/10 text-[#7d64ff] font-medium' : 'bg-gray-100 text-gray-400'}`}>{d}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="slot-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Slot' : 'Add Delivery Slot'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Slot Name</Label><Input data-testid="slot-name" value={form.slot_name} onChange={e => setForm(f => ({ ...f, slot_name: e.target.value }))} placeholder="Morning Express" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} /></div>
                <div><Label>End</Label><Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} /></div>
              </div>
              <div><Label>Max Orders</Label><Input type="number" value={form.max_orders} onChange={e => setForm(f => ({ ...f, max_orders: parseInt(e.target.value) || 50 }))} /></div>
              <div><Label>Active Days</Label>
                <div className="flex gap-2 mt-1">{DAYS.map(d => <button key={d} type="button" onClick={() => toggleDay(d)} className={`text-xs px-2 py-1 rounded-lg border ${form.days.includes(d) ? 'bg-[#7d64ff] text-white border-[#7d64ff]' : 'border-gray-300 text-gray-500'}`}>{d}</button>)}</div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" /> Active</label>
              <Button data-testid="save-slot-btn" onClick={handleSave} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">{editing ? 'Update' : 'Create'} Slot</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
